import _ from 'lodash';
import { required, requiredIf } from 'vuelidate/lib/validators';
import { toolkitMixin } from '@/app/mixins/toolkit/item';
import { pensionSetupMixin } from '@/app/mixins/toolkit/pension/setup';
import WarningModal from '@/app/modules/_shared/modals/warning-modal/warning-modal.vue';
import partMatrix from '@/app/utils/toolkit/business-rules/pension-establishment/partMatrix';
import { getDefaultItem } from '@/app/utils/toolkit/defaultItems';

const options = [
  {
    value: () => 'The whole balance of the pensioner\'s accumulation account',
    tooltip: null,
  },
  {
    value: () => 'The balance of the pensioner\'s accumulation account less a fixed amount',
    tooltip: null,
  },
  {
    value: () => 'As much as possible without going over the transfer balance cap',
    tooltip: 'The transfer balance cap is a cap on the amount anyone can put into "retirement phase pensions" over their lifetime. It started at $1.6m at 1 July 2017. Retirement phase pensions are all superannuation pensions other than transition to retirement pensions. They are called retirement phase pensions because they are usually paid to people who are considered to have "retired" (sometimes permanently but sometimes only temporarily) under superannuation law. In contrast, transition to retirement pensions are paid to people who haven\'t retired or satisfied some other condition allowing them unlimited access to their superannuation (for example, reaching age 65).',
  },
  {
    value: () => 'A fixed amount',
    tooltip: null,
  },
];

export default {
  name: 'pension-setup',
  mixins: [toolkitMixin, pensionSetupMixin],
  components: {
    WarningModal,
  },
  data: () => ({
    showResetWarning: false,
    clickedStep: null,
    setup: {
      howMuch: '',
      accumulationKnown: '',
      taxComponentKnown: '',
      transferBalanceCapKnown: '',
    },
  }),
  computed: {
    howMuchOptions() {
      return options.filter((option, idx) => {
        if (this.isTris) {
          return idx !== 2;
        }
        return true;
      });
    },
    hasAccumulation() {
      return this.getItem.accumulation.amount > 0;
    },
    hasPension() {
      return this.getItem.pensions.length > 0;
    },
    isPartOneOnly() {
      return partMatrix(this.setup, this.setup.transferBalanceCapKnown) === 'PART_1';
    },
    isTris() {
      return this.getItem.requirements.eligibility.reasons === 'The pension will be a transition to retirement pension';
    },
    taxComponentRequired() {
      return (this.setup.accumulationKnown === 'No' && (this.setup.howMuch === 'As much as possible without going over the transfer balance cap' || this.setup.howMuch === 'A fixed amount'));
    },
    transferBalanceCapKnownRequired() {
      return (this.setup.howMuch === 'As much as possible without going over the transfer balance cap' && this.setup.accumulationKnown !== '');
    },
  },
  methods: {
    resetAccumAct() {
      const accumulation = _.cloneDeep(getDefaultItem('PENSION_ESTABLISHMENT').accumulation);
      return this.setItem({ accumulation });
    },
    resetPensionAmounts() {
      const pensions = _.cloneDeep(this.getItem.pensions);
      pensions.forEach((pension) => {
        pension.amount = 0;
        pension.fixedAmount = 0;
      });
      return this.setItem({ pensions });
    },
    handleHowMuchChange() {
      this.setup.accumulationKnown = '';
      this.setup.taxComponentKnown = '';
      this.setup.transferBalanceCapKnown = '';
    },
    handleAcummKnownChange() {
      this.setup.taxComponentKnown = '';
      this.setup.transferBalanceCapKnown = '';
    },
    replaceLocalWithStore() {
      this.setup.howMuch = this.getItem.setup.howMuch;
      this.setup.accumulationKnown = this.getItem.setup.accumulationKnown;
      this.setup.taxComponentKnown = this.getItem.setup.taxComponentKnown;
      this.setup.transferBalanceCapKnown = this.getItem.setup.transferBalanceCapKnown;
    },
    hasSetupChanged() {
      const localSetup = {
        howMuch: this.setup.howMuch,
        accumulationKnown: this.setup.accumulationKnown,
        taxComponentKnown: this.setup.taxComponentKnown,
        transferBalanceCapKnown: this.setup.transferBalanceCapKnown,
      };
      const storeSetup = {
        howMuch: this.getItem.setup.howMuch,
        accumulationKnown: this.getItem.setup.accumulationKnown,
        taxComponentKnown: this.getItem.setup.taxComponentKnown,
        transferBalanceCapKnown: this.getItem.setup.transferBalanceCapKnown,
      };
      return !_.isEqual(localSetup, storeSetup);
    },
    async handleResetWarning(action) {
      if (action === 'confirm') {
        await this.setItem({
          setup: {
            howMuch: this.setup.howMuch,
            accumulationKnown: this.setup.accumulationKnown,
            taxComponentKnown: this.setup.taxComponentKnown,
            transferBalanceCapKnown: this.setup.transferBalanceCapKnown,
          },
        });
        await this.setPartValue(this.setup);
        await this.resetAccumAct();
        await this.resetPensionAmounts();
        if (this.hasPension && this.getItem.setup.part === 'PART_2_FULL') {
          await this.setPensionStatus('NEEDS_REVIEW');
        } else {
          await this.setPensionStatus('IN_PROGRESS');
        }
        this.$emit(this.clickedStep);
        this.clickedStep = null;
      } else if (action === 'cancel') {
        this.replaceLocalWithStore();
      }
      this.showResetWarning = false;
    },
    async handleStepChange(step) {
      if (this.hasSetupChanged() && (this.hasAccumulation || this.hasPension)) {
        this.showResetWarning = true;
        this.clickedStep = step;
      } else {
        await this.setItem({
          setup: {
            howMuch: this.setup.howMuch,
            accumulationKnown: this.setup.accumulationKnown,
            taxComponentKnown: this.setup.taxComponentKnown,
            transferBalanceCapKnown: this.setup.transferBalanceCapKnown,
          },
        });
        await this.setPartValue(this.setup);
        this.$emit(step);
      }
    },
    async handleBackStep() {
      await this.handleStepChange('backStep');
    },
    async handleNextStep() {
      await this.handleStepChange('nextStep');
    },
  },
  activated() {
    this.replaceLocalWithStore();
  },
  mounted() {
    this.replaceLocalWithStore();
  },
  validations: {
    setup: {
      howMuch: {
        required,
      },
      accumulationKnown: {
        required,
      },
      taxComponentKnown: {
        required: requiredIf(function requiredCheck() {
          return this.taxComponentRequired;
        }),
      },
      transferBalanceCapKnown: {
        required: requiredIf(function requiredCheck() {
          return this.transferBalanceCapKnownRequired;
        }),
      },
    },
  },
};
